import { nanoid } from 'nanoid';

import { type SelectionRange } from '../../../state/types.ts';

import { groupContiguous } from './group-contiguous.ts';
import { reconcileIndices } from './reconcile-indices.ts';
import { type ReconcileContext } from './types.ts';

/**
 * Reconciles a single cell-mode range after a structural change.
 *
 * Both axes are expanded independently and the result is the cross-product
 * of contiguous row groups × contiguous column groups, so a single rectangle
 * can split into multiple rectangles when gaps appear on either axis.
 *
 * The first output range preserves the ID of the input range;
 * any additional ranges generated from splits receive new IDs.
 *
 * @example
 * ```ts
 * // A 3×1 cell range covering rows [0, 1, 2], col [0].
 * // Row 'b' (index 1) is deleted:
 * // prev entries: ['a', 'b', 'c'] → next: ['a', 'c']
 * // Rows 'a' → 0 and 'c' → 1 are contiguous → single range [0..1, 0..0]
 *
 * // A 3×1 cell range covering rows [0, 1, 2], col [0].
 * // A new row 'NEW' is inserted between 'a' and 'b':
 * // prev entries: ['a', 'b', 'c'] → next: ['a', 'NEW', 'b', 'c']
 * // Row 'a' → 0 (group 1), 'b' → 2, 'c' → 3 (group 2) → two ranges
 * ```
 */
export function reconcileCellRange({
  range,
  prevEntries,
  nextEntriesLookup,
  prevFields,
  nextFieldsLookup
}: ReconcileContext & {
  range: SelectionRange;
}): SelectionRange[] {
  const rowMin = Math.min(range.anchor.row.index, range.lead.row.index);
  const rowMax = Math.max(range.anchor.row.index, range.lead.row.index);
  const prevRowIndices: number[] = [];

  for (let i = rowMin; i <= rowMax; i++) {
    prevRowIndices.push(i);
  }

  const colMin = Math.min(range.anchor.col.index, range.lead.col.index);
  const colMax = Math.max(range.anchor.col.index, range.lead.col.index);
  const prevColIndices: number[] = [];

  for (let i = colMin; i <= colMax; i++) {
    prevColIndices.push(i);
  }

  const newRowIndices = reconcileIndices(prevRowIndices, prevEntries, nextEntriesLookup);
  const newColIndices = reconcileIndices(prevColIndices, prevFields, nextFieldsLookup);

  const rowGroups = groupContiguous(newRowIndices);
  const colGroups = groupContiguous(newColIndices);

  if (rowGroups.length === 0 || colGroups.length === 0) {
    return [];
  }

  const ranges: SelectionRange[] = [];
  let isFirst = true;

  for (const rowGroup of rowGroups) {
    for (const colGroup of colGroups) {
      ranges.push({
        id: isFirst ? range.id : nanoid(),
        anchor: {
          row: { index: rowGroup[0] },
          col: { index: colGroup[0] }
        },
        lead: {
          row: { index: rowGroup[rowGroup.length - 1] },
          col: { index: colGroup[colGroup.length - 1] }
        }
      });
      isFirst = false;
    }
  }

  return ranges;
}
